/* eslint-disable react-hooks/rules-of-hooks */
import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { parseDate } from "@lyfegen-platform/utils";
import { Box, Typography } from "@material-ui/core";
import { addDays, isValid, subDays } from "date-fns";
import differenceInDays from "date-fns/differenceInDays";
import { useField, useFieldValue } from "formik";
import { max } from "lodash";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { ControlState } from "../models/flexible/control-state";
import { combinePaths } from "../models/flexible/utils";
import { CoverageRequestTherapyDecision } from "../models/generated/graphql";
import { getPathPrefix } from "../utils/get-path-prefix";
import { localeDate } from "../utils/locale-date";
import { getCoverageBoundaries } from "../utils/negotiation-rounds";
import { renderRequiredMessage } from "../utils/render-required-message";
import { GeneralFieldController } from "./general-field-controller";
import { GetPatientEntitiesByEntityId } from "./types/GetPatientEntitiesByEntityId";
import { GetPatientEntitiesById } from "./types/GetPatientEntitiesById";

type InitialValuesType = GetPatientEntitiesByEntityId | GetPatientEntitiesById;

const DateDifference = () => {
	const [{ value: therapyTrialStartDateStr }] = useField("therapyTrialStartDate");
	const [{ value: therapyTrialEndDateStr }] = useField("therapyTrialEndDate");

	const therapyTrialStartDate = parseDate(therapyTrialStartDateStr);
	const therapyTrialEndDate = parseDate(therapyTrialEndDateStr);

	return isValid(therapyTrialStartDate) && isValid(therapyTrialEndDate) && therapyTrialEndDate > therapyTrialStartDate ? (
		<>{differenceInDays(therapyTrialEndDate, therapyTrialStartDate)}</>
	) : (
		<>n/a</>
	);
};

const DateDifferenceRoot = () => {
	const { t } = useTranslation();

	return (
		<Box alignSelf={"center"}>
			<Typography>{t("duration")}</Typography>
			<Box>
				<DateDifference />
				{` ${t("days-of-therapy-trial")}`}
			</Box>
		</Box>
	);
};

export class TherapyTrialEndDateFieldController extends GeneralFieldController {
	override useControlState(path: string): ControlState {
		const [decisionTherapy] = useFieldValue(combinePaths(getPathPrefix(path, 1), AttributeIdentifiers.DecisionTherapy));

		return decisionTherapy === CoverageRequestTherapyDecision.Trial ? "enabled" : "hidden";
	}

	override createValidationRule(_initialData: InitialValuesType, translate: (key: string) => string): Yup.AnySchema {
		const coverageBoundaries = this.parent.context.cases.flatMap(getCoverageBoundaries);
		const existingTherapyTrialEndDateString = max(
			coverageBoundaries
				.filter(
					({ formCreatedOn }) =>
						!this.parent.context.flexEntity?.createdOn || formCreatedOn < this.parent.context.flexEntity?.createdOn
				)
				.filter(({ decision }) => decision === CoverageRequestTherapyDecision.Trial)
				.map(({ endDate }) => endDate)
		);
		const coverageStartDate = coverageBoundaries.find(
			({ decision }) => decision === CoverageRequestTherapyDecision.Approved
		)?.startDate;

		const existingTherapyTrialEndDate = existingTherapyTrialEndDateString && parseDate(existingTherapyTrialEndDateString);

		return super
			.createFieldValidationRuleByDataType(translate)
			.when(AttributeIdentifiers.DecisionTherapy, {
				is: CoverageRequestTherapyDecision.Trial,
				then: (schema: Yup.AnySchema) =>
					coverageStartDate
						? (schema as Yup.DateSchema).max(
								subDays(parseDate(coverageStartDate) as Date, 1),
								`${translate("cannot-be-greater-then")} ${localeDate(subDays(parseDate(coverageStartDate) as Date, 1))}`
						  )
						: schema,
			})
			.when([AttributeIdentifiers.DecisionTherapy, AttributeIdentifiers.TherapyTrialStartDate], ((
				therapyDecision: CoverageRequestTherapyDecision,
				therapyTrialStartDate: Date,
				schema: Yup.DateSchema<any>
			) => {
				if (therapyDecision === CoverageRequestTherapyDecision.Approved && !therapyTrialStartDate) {
					return schema;
				}

				if (therapyDecision === CoverageRequestTherapyDecision.Trial && !therapyTrialStartDate) {
					return schema.nullable().required(renderRequiredMessage(this.schema.attribute.identifier, translate));
				}

				if (
					therapyDecision === CoverageRequestTherapyDecision.Trial &&
					!!therapyTrialStartDate &&
					existingTherapyTrialEndDate &&
					isValid(existingTherapyTrialEndDate)
				) {
					return schema
						.min(
							existingTherapyTrialEndDate.toDateString(),
							`${translate("cannot-be-less-than-initial-end-date")} ${existingTherapyTrialEndDateString}`
						)
						.nullable()
						.required(renderRequiredMessage(this.schema.attribute.identifier, translate));
				}

				if (therapyDecision === CoverageRequestTherapyDecision.Trial && !!therapyTrialStartDate) {
					return schema
						.min(
							addDays(therapyTrialStartDate, 1),
							`${translate("cannot-be-less-or-equal-than")} ${translate("therapyTrialStartDate")}`
						)
						.nullable()
						.required(renderRequiredMessage(this.schema.attribute.identifier, translate));
				}

				return schema;
			}) as any);
	}

	renderAfter(): React.ReactElement {
		return <DateDifferenceRoot />;
	}
}
